import type { Dispatch, Middleware } from "@reduxjs/toolkit";
import type { IRootReducerState } from "@/store/IRootReducer";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import { authService } from "@/utils/authService";
import { portalsApi } from "./portalsApi";
import { presenceReceived, typingCleared, typingReceived } from "../slices/employeePresenceSlice";
import { logger } from "@/logger-init";

type WSEventType =
  | "portal:comment:created"
  | "portal:attachment:created"
  | "portal:item:created"
  | "portal:item:merged"
  | "portal:workflow:updated"
  | "ticket:updated"
  | "emp:notification:created"
  | "emp:message:created"
  | "emp:message:updated"
  | "emp:message:read"
  | "emp:typing"
  | "emp:presence"
  | "connection:established"
  | "pong";

interface WSMessage {
  type: WSEventType;
  instanceId: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

const MAX_RECONNECT_DELAY = 30_000;
const INITIAL_RECONNECT_DELAY = 1_000;
const STABLE_CONNECTION_THRESHOLD = 5_000;
/** A tab away for longer than this catches up when it is shown again. */
const CATCH_UP_AFTER_HIDDEN_MS = 15_000;

let ws: WebSocket | null = null;
let reconnectDelay = INITIAL_RECONNECT_DELAY;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let backoffResetTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;
let lastToken: string | undefined;
/**
 * The instance the open socket actually joined.
 *
 * The server files each socket into a room keyed by instance, and only
 * broadcasts into the room for the instance an event belongs to. So a socket
 * opened on one instance hears nothing after the user switches to another —
 * the connection stays up and looks healthy, while every event goes to a room
 * it is not in. The symptom is "nothing arrives until I refresh", because a
 * refresh is the only thing that reconnects.
 */
let lastInstanceId: string | null | undefined;
/** Guards the MSAL fallback: the middleware runs on every dispatched action. */
let acquiring = false;
/** Whether a socket has opened before, so the next open is a reconnect. */
let hasConnectedBefore = false;
/** When the tab was last hidden (0 while shown). */
let hiddenAt = 0;
/** Window listeners are attached once per page. */
let pageListenersAttached = false;

/**
 * Refetch what live events keep fresh: the conversation list and unread count,
 * open threads, and the bell.
 *
 * Live events can be missed — while the socket was down (wifi, sleep, a
 * deploy) or while the tab was in the background — and nothing replays them.
 * So after a reconnect, and when a tab comes back after a while, ask the
 * server again. Only queries something on screen is using refetch, so this is
 * a few small requests, not a reload.
 */
function catchUp(dispatch: Dispatch): void {
  dispatch(
    portalsApi.util.invalidateTags([
      { type: "EmployeeConversations", id: "LIST" },
      "EmployeeMessages",
      { type: "EmployeeNotifications", id: "LIST" },
      { type: "EmployeeNotifications", id: "COUNT" },
      "EmployeeBroadcastChannel",
    ]),
  );
}

/** Up to this long after a reconnect before catching up, so a deploy doesn't send every tab at once. */
const CATCH_UP_JITTER_MS = 3_000;

/**
 * Whether a token can still be used, judged from its own expiry. A token that
 * can't be read is given the benefit of the doubt: the server decides.
 */
function stillValid(token: string): boolean {
  try {
    const part = token.split(".")[1];
    if (!part) return true;
    const claims = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: unknown;
    };
    return typeof claims.exp !== "number" || claims.exp * 1000 > Date.now() + 30_000;
  } catch {
    return true;
  }
}

function cleanup(): void {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (backoffResetTimer) {
    clearTimeout(backoffResetTimer);
    backoffResetTimer = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
  reconnectDelay = INITIAL_RECONNECT_DELAY;
}

export const websocketMiddleware: Middleware = (storeApi) => {
  function handleMessage(msg: WSMessage): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dispatch = storeApi.dispatch as (action: any) => any;

    switch (msg.type) {
      case "emp:notification:created": {
        // The push carries no content: refetch the bell from the database.
        dispatch(
          portalsApi.util.invalidateTags([
            { type: "EmployeeNotifications", id: "LIST" },
            { type: "EmployeeNotifications", id: "COUNT" },
          ]),
        );
        break;
      }
      case "emp:message:created":
      case "emp:message:updated":
      case "emp:message:read": {
        // The event carries the message, but we invalidate rather than write it
        // into the cache. broadcastToUsers reaches only sockets on the sending
        // server and its Redis publish is best-effort, so the stream can have
        // gaps; refetching is what closes them. The socket says "something
        // changed", the database says what.
        const conversationId = msg.payload.conversationId as string | undefined;
        // Their message arrived, so they have stopped typing. Waiting for the
        // entry to age out leaves the bubble sitting under a message that is
        // already on screen.
        const sender = (msg.payload.message as { senderPartyId?: string } | undefined)
          ?.senderPartyId;
        if (conversationId && sender) {
          dispatch(typingCleared({ conversationId, partyId: sender }));
        }
        dispatch(
          portalsApi.util.invalidateTags([
            { type: "EmployeeConversations", id: "LIST" },
            ...(conversationId
              ? [
                  { type: "EmployeeMessages" as const, id: conversationId },
                  { type: "EmployeeConversations" as const, id: conversationId },
                ]
              : []),
          ]),
        );
        break;
      }
      case "emp:typing": {
        // Deliberately not cache invalidation: refetching a thread every few
        // seconds because somebody is typing would be a self-inflicted load
        // problem in exchange for a three-dot animation.
        const conversationId = msg.payload.conversationId as string | undefined;
        const partyId = msg.payload.partyId as string | undefined;
        if (conversationId && partyId) {
          dispatch(typingReceived({ conversationId, partyId, at: Date.now() }));
        }
        break;
      }
      case "emp:presence": {
        const partyId = msg.payload.partyId as string | undefined;
        if (partyId) {
          dispatch(
            presenceReceived({
              partyId,
              online: msg.payload.online === true,
              lastSeenAt: (msg.payload.lastSeenAt as string | null) ?? null,
            }),
          );
        }
        break;
      }
      case "portal:comment:created": {
        const itemKey = msg.payload.itemKey as string | undefined;
        const portalKey = msg.payload.portalKey as string | undefined;
        if (itemKey) {
          const tagsToInvalidate: { type: "WorkItemComment" | "WorkItem"; id: string }[] = [
            { type: "WorkItemComment", id: itemKey },
            { type: "WorkItem", id: `HISTORY_${itemKey}` },
          ];
          // Also invalidate customer portal detail cache for real-time updates
          if (portalKey) {
            tagsToInvalidate.push({
              type: "WorkItem",
              id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}`,
            });
          }
          dispatch(portalsApi.util.invalidateTags(tagsToInvalidate));
        }
        break;
      }

      case "portal:attachment:created": {
        const itemKey = msg.payload.itemKey as string | undefined;
        if (itemKey) {
          dispatch(
            portalsApi.util.invalidateTags([{ type: "WorkItem", id: `ATTACHMENTS_${itemKey}` }]),
          );
        }
        break;
      }

      case "portal:item:created": {
        const portalKey = msg.payload.portalKey as string | undefined;
        if (portalKey) {
          dispatch(
            portalsApi.util.invalidateTags([
              { type: "WorkItem", id: `LIST_${portalKey}` },
              { type: "WorkItem", id: `ALL_${portalKey}` },
              { type: "PortalBoard", id: portalKey },
              { type: "Queue", id: portalKey },
              { type: "Portal", id: "DASHBOARD" },
            ]),
          );
        }
        break;
      }

      // A merge changes both tickets at once — refresh whichever one the
      // viewer happens to have open (#937).
      case "portal:item:merged": {
        const itemKey = msg.payload.itemKey as string | undefined;
        const targetItemKey = msg.payload.targetItemKey as string | undefined;
        const portalKey = msg.payload.portalKey as string | undefined;
        const targetPortalKey = msg.payload.targetPortalKey as string | undefined;
        const tagsToInvalidate: { type: "WorkItem" | "WorkItemComment"; id: string }[] = [];
        for (const key of [itemKey, targetItemKey]) {
          if (!key) continue;
          tagsToInvalidate.push(
            { type: "WorkItem", id: key },
            { type: "WorkItemComment", id: key },
            { type: "WorkItem", id: `ATTACHMENTS_${key}` },
            { type: "WorkItem", id: `HISTORY_${key}` },
            { type: "WorkItem", id: `LINKS_${key}` },
          );
        }
        for (const key of [portalKey, targetPortalKey]) {
          if (!key) continue;
          tagsToInvalidate.push(
            { type: "WorkItem", id: `LIST_${key}` },
            { type: "WorkItem", id: `ALL_${key}` },
          );
        }
        if (tagsToInvalidate.length > 0) {
          dispatch(portalsApi.util.invalidateTags(tagsToInvalidate));
        }
        break;
      }

      case "portal:workflow:updated": {
        const portalKey = msg.payload.portalKey as string | undefined;
        if (portalKey) {
          dispatch(
            portalsApi.util.invalidateTags([
              { type: "PortalWorkflow", id: portalKey },
              { type: "PortalBoard", id: portalKey },
            ]),
          );
        }
        break;
      }

      case "ticket:updated": {
        const itemKey = msg.payload.itemKey as string | undefined;
        const portalKey = msg.payload.portalKey as string | undefined;
        if (itemKey) {
          const tagsToInvalidate: { type: "WorkItem"; id: string }[] = [
            { type: "WorkItem", id: itemKey },
            { type: "WorkItem", id: `HISTORY_${itemKey}` },
          ];
          if (portalKey) {
            tagsToInvalidate.push(
              { type: "WorkItem", id: `LIST_${portalKey}` },
              { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
            );
          }
          dispatch(portalsApi.util.invalidateTags(tagsToInvalidate));
        }
        break;
      }
    }
  }

  function connect(token: string, retrying = false): void {
    const instanceId = getInstanceIdFromUrl();
    logger.debug("[WS Middleware] connect called", { instanceId, hasToken: !!token });
    if (!instanceId || !token) return;

    // cleanup() resets the backoff; a retry must keep it, or every retry
    // after an outage waits the initial second and the backoff never grows.
    const delay = reconnectDelay;
    cleanup();
    if (retrying) reconnectDelay = delay;
    intentionalClose = false;

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.host;
    const url = `${protocol}://${host}/ws?token=${encodeURIComponent(token)}&instanceId=${encodeURIComponent(instanceId)}`;
    logger.debug("[WS Middleware] Connecting to", { url: url.split("?")[0] });

    try {
      ws = new WebSocket(url);

      ws.onopen = () => {
        logger.info("[WS Middleware] Connected");
        // Anything sent while the socket was down never arrives as an event.
        if (hasConnectedBefore) {
          setTimeout(() => catchUp(storeApi.dispatch), Math.random() * CATCH_UP_JITTER_MS);
        }
        hasConnectedBefore = true;

        // Delay backoff reset — only reset if connection stays open for 5s
        if (backoffResetTimer) {
          clearTimeout(backoffResetTimer);
        }
        backoffResetTimer = setTimeout(() => {
          backoffResetTimer = null;
          reconnectDelay = INITIAL_RECONNECT_DELAY;
        }, STABLE_CONNECTION_THRESHOLD);
      };

      ws.onmessage = (event) => {
        try {
          const msg: WSMessage = JSON.parse(event.data);
          logger.debug("[WS Middleware] Received", { type: msg.type, payload: msg.payload });
          handleMessage(msg);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        logger.info("[WS Middleware] Disconnected", { code: event.code, reason: event.reason });
        ws = null;

        // Clear the backoff reset timer since the connection didn't stay stable
        if (backoffResetTimer) {
          clearTimeout(backoffResetTimer);
          backoffResetTimer = null;
        }

        // Don't reconnect if the close was triggered by cleanup
        if (intentionalClose) return;

        scheduleReconnect(token);
      };

      ws.onerror = (event) => {
        logger.error("[WS Middleware] Error");
      };
    } catch {
      if (!intentionalClose) {
        scheduleReconnect(token);
      }
    }
  }

  /**
   * Open the socket, acquiring a token the same way the API does.
   *
   * Waiting on `authReducer.accessToken` alone is why this never connected:
   * that slice is not persisted and nothing reliably populates it on a plain
   * page load, so the condition was simply never true. `prepareHeaders` in
   * baseApi already has this fallback — and its comment says exactly why —
   * which is how every REST call kept working while the socket stayed shut and
   * silent. The symptom was real-time that only ever worked after some other
   * request happened to refresh the cache.
   */
  async function ensureConnected(reduxToken: string | undefined): Promise<void> {
    const instanceId = getInstanceIdFromUrl();

    if (!reduxToken && lastToken && !instanceId) {
      // Signed out, or navigated somewhere with no instance.
      lastToken = undefined;
      lastInstanceId = undefined;
      cleanup();
      return;
    }
    if (!instanceId) return;

    let token = reduxToken;
    if (!token) {
      // One attempt at a time: the middleware runs on every dispatched action,
      // and MSAL would otherwise be asked for a token dozens of times a second.
      if (acquiring) return;
      acquiring = true;
      try {
        token = await authService.getAccessToken();
      } catch {
        // MSAL not ready yet; a later action will try again.
      } finally {
        acquiring = false;
      }
    }
    if (!token) return;
    if (token === lastToken && instanceId === lastInstanceId) return;

    logger.debug("[WS Middleware] Connecting WebSocket", {
      reason: token !== lastToken ? "token" : "instance changed",
      instanceId,
    });
    lastToken = token;
    lastInstanceId = instanceId;
    connect(token);
  }

  function scheduleReconnect(token: string): void {
    if (reconnectTimer) return;
    const delay = reconnectDelay;
    reconnectDelay = Math.min(delay * 2, MAX_RECONNECT_DELAY);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void (async () => {
        // The store's token is often empty (see ensureConnected), so ask MSAL
        // as the first connection did; reading the store alone left a dropped
        // socket closed until the page was reloaded.
        const state = storeApi.getState() as IRootReducerState;
        let currentToken: string | undefined = state.authReducer?.accessToken;
        if (!currentToken) {
          try {
            currentToken = await authService.getAccessToken();
          } catch {
            // MSAL not ready; fall back to the token the socket last used.
          }
        }
        // ...but only while it is still good: retrying forever with an expired
        // one just gets refused forever. Stop instead; the next token the app
        // gets (any request refreshes it) reconnects through ensureConnected.
        if (!currentToken && stillValid(token)) currentToken = token;
        if (!currentToken) {
          lastToken = undefined;
          return;
        }
        if (intentionalClose || ws) return;
        lastToken = currentToken;
        connect(currentToken, true);
      })();
    }, delay);
  }

  if (!pageListenersAttached && typeof window !== "undefined" && typeof document !== "undefined") {
    pageListenersAttached = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const awayFor = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = 0;
      if (!lastToken) return;
      if (awayFor >= CATCH_UP_AFTER_HIDDEN_MS) catchUp(storeApi.dispatch);
      if (!ws && !reconnectTimer && !intentionalClose) {
        reconnectDelay = INITIAL_RECONNECT_DELAY;
        scheduleReconnect(lastToken);
      }
    });
    // Back on the network: try now rather than at the end of a long backoff.
    window.addEventListener("online", () => {
      if (!lastToken || ws || intentionalClose) return;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      reconnectDelay = INITIAL_RECONNECT_DELAY;
      scheduleReconnect(lastToken);
    });
  }

  return (next) => (action) => {
    const result = next(action);

    // Watch for token changes to connect/disconnect
    const state = storeApi.getState() as IRootReducerState;
    const token = state.authReducer?.accessToken;

    void ensureConnected(token);

    return result;
  };
};
